import { useState } from "react";
import { motion } from "framer-motion";
import {
  MagnifyingGlass, Lightning, Envelope, ChartBar, Crosshair,
  ArrowRight, Check, X, CaretDown, Buildings, Globe, Target,
  Megaphone, DownloadSimple, ShieldCheck, Funnel, Clock,
  ArrowsClockwise, Cpu, Users, CurrencyDollar, ArrowDown
} from "@phosphor-icons/react";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5 } };

/* ── Inline product mockup (real UI, not an image) ── */
function ProductMockup() {
  return (
    <div className="bg-gray-900 border border-gray-700 shadow-2xl overflow-hidden text-left text-sm" data-testid="product-mockup">
      {/* Tab bar */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-800 border-b border-gray-700">
        <div className="flex gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-400" /><span className="w-2.5 h-2.5 rounded-full bg-yellow-400" /><span className="w-2.5 h-2.5 rounded-full bg-green-400" /></div>
        <span className="ml-3 text-[10px] text-gray-500">pluckm.com/app</span>
      </div>
      <div className="flex" style={{ minHeight: 340 }}>
        {/* Sidebar */}
        <div className="w-48 bg-gray-800/50 border-r border-gray-700 p-3 flex-shrink-0">
          <p className="text-[9px] font-bold uppercase tracking-wider text-blue-400 mb-2">Campaign</p>
          <div className="bg-blue-500/10 border border-blue-500/30 px-2 py-1.5 mb-1"><span className="text-[11px] text-blue-300 font-medium">Local SEO Outreach</span></div>
          <div className="px-2 py-1.5 mb-3"><span className="text-[11px] text-gray-500">Reputation Builder</span></div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-2">Find Prospects</p>
          <div className="bg-gray-700/50 px-2 py-1.5 mb-1.5 text-[11px] text-gray-300">Dentist</div>
          <div className="flex gap-1 mb-2">
            <div className="bg-gray-700/50 px-2 py-1.5 text-[11px] text-gray-300 flex-1">Nashville</div>
            <div className="bg-gray-700/50 px-2 py-1.5 text-[11px] text-gray-400 w-10">TN</div>
          </div>
          <div className="bg-blue-500 text-center py-1.5 text-[10px] text-white font-medium">Discover</div>
        </div>
        {/* Prospect list */}
        <div className="w-56 border-r border-gray-700 flex-shrink-0">
          <div className="px-3 py-2 border-b border-gray-700 text-[10px] text-gray-400 font-medium">Prospects (11)</div>
          {[
            { name: "Nashville Family Dental", score: 91, color: "text-emerald-400" },
            { name: "Midtown Smiles", score: 58, color: "text-yellow-400", active: true },
            { name: "Green Hills Dentistry", score: 44, color: "text-red-400" },
            { name: "East Nashville Dental", score: 76, color: "text-emerald-400" },
            { name: "Belmont Dental Care", score: 35, color: "text-red-400" },
          ].map((p, i) => (
            <div key={i} className={`px-3 py-2.5 border-b border-gray-700/50 ${p.active ? "bg-blue-500/10 border-l-2 border-l-blue-400" : "border-l-2 border-l-transparent"}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-gray-200 font-medium truncate">{p.name}</span>
                <span className={`text-[10px] font-bold ${p.color}`}>{p.score}</span>
              </div>
              <span className="text-[9px] text-gray-500">Nashville, TN</span>
            </div>
          ))}
        </div>
        {/* Detail panel */}
        <div className="flex-1 p-4 bg-gray-800/30">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-bold text-white">Midtown Smiles</p>
              <p className="text-[10px] text-gray-500">midtownsmilesnashville.com</p>
            </div>
            <div className="bg-yellow-400/10 border border-yellow-400/30 px-2 py-1 text-center">
              <span className="text-lg font-black text-yellow-400 leading-none">58</span>
              <span className="text-[8px] text-yellow-400/70 block">/100</span>
            </div>
          </div>
          {/* Mini audit */}
          <div className="space-y-1.5 mb-4">
            {[
              { label: "SSL Certificate", ok: true },
              { label: "Page Load: 3.8s", ok: false },
              { label: "Mobile Viewport", ok: true },
              { label: "Meta Description", ok: false },
              { label: "Structured Data", ok: false },
            ].map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                {c.ok ? <Check size={10} className="text-emerald-400" weight="bold" /> : <X size={10} className="text-red-400" weight="bold" />}
                <span className={`text-[10px] ${c.ok ? "text-gray-400" : "text-gray-300"}`}>{c.label}</span>
              </div>
            ))}
          </div>
          {/* Email preview */}
          <div className="bg-gray-700/40 border border-gray-600/50 p-3">
            <p className="text-[9px] font-bold uppercase tracking-wider text-blue-400 mb-1.5">Generated Email</p>
            <p className="text-[10px] text-gray-300 font-medium mb-1">Subject: A few things I noticed on your site</p>
            <p className="text-[10px] text-gray-400 leading-relaxed">Hi — I took a look at midtownsmilesnashville.com and noticed your page loads in 3.8s on mobile (patients leave after 3), there's no meta description, and no structured data — so your hours and address may not show in local search results. Your overall score is 58/100. Happy to send the full breakdown.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Workflow walkthrough (trust section) ── */
function WorkflowWalkthrough() {
  const steps = [
    { label: "Choose campaign", detail: "Define your offer once", icon: Megaphone, color: "#002FA7" },
    { label: "Find prospects", detail: "Dentists in Nashville, TN", icon: Crosshair, color: "#002FA7" },
    { label: "Audit sites", detail: "Scores: 91, 58, 44...", icon: Lightning, color: "#D97706" },
    { label: "Generate email", detail: "Personalized per prospect", icon: Envelope, color: "#059669" },
  ];
  return (
    <div className="flex items-center justify-center gap-2 md:gap-4 flex-wrap">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-2 md:gap-4">
          <div className="text-center">
            <div className="w-14 h-14 mx-auto mb-2 border-2 flex items-center justify-center" style={{ borderColor: s.color + "40", background: s.color + "08" }}>
              <s.icon size={22} style={{ color: s.color }} weight="duotone" />
            </div>
            <p className="text-xs font-bold text-gray-900">{s.label}</p>
            <p className="text-[10px] text-gray-400">{s.detail}</p>
          </div>
          {i < steps.length - 1 && <ArrowRight size={16} className="text-gray-300 mt-[-20px]" />}
        </div>
      ))}
    </div>
  );
}

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-[#FAFBFC] scroll-smooth" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* ── NAV ── */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100" data-testid="nav">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <a href="/" className="text-xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            <span className="text-[#002FA7]">Pluck</span><span className="text-gray-900">m</span>
          </a>
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-500">
            <a href="#how" className="hover:text-gray-900 transition-colors">How it works</a>
            <a href="#features" className="hover:text-gray-900 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-gray-900 transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <a href="/app" className="text-sm text-gray-500 hover:text-gray-900 transition-colors hidden sm:block">Log in</a>
            <a href="#pricing" className="bg-[#002FA7] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#002482] transition-all" data-testid="nav-cta">
              Get Early Access
            </a>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 px-4 sm:px-6" data-testid="hero">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fade} className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tight text-gray-900 leading-[1.08]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Find local businesses.<br />
              Spot what's broken.<br />
              <span className="text-[#002FA7]">Send outreach that gets replies.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg md:text-xl text-gray-500 max-w-2xl leading-relaxed">
              Discover local businesses in any niche and city, run real website audits, and generate personalized cold emails from the actual findings — in minutes, not hours.
            </p>
            <p className="mt-3 text-sm text-gray-400 font-medium">
              For agencies, freelancers, and SaaS founders selling SEO, booking, visibility, and web services to local businesses.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a href="#pricing" className="bg-[#002FA7] text-white px-8 py-3.5 text-sm font-semibold hover:bg-[#002482] transition-all flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="hero-cta">
                Get Early Access <ArrowRight size={16} weight="bold" />
              </a>
              <a href="#how" className="text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium">
                See how it works
              </a>
            </div>
          </motion.div>

          {/* Proof strip — stronger visibility */}
          <motion.div {...fade} transition={{ delay: 0.15 }} className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 py-4 px-4 sm:px-5 bg-white border border-gray-200">
            {[
              { icon: Crosshair, text: "Built for local prospecting" },
              { icon: ShieldCheck, text: "Real website audits" },
              { icon: Envelope, text: "Personalized outreach" },
              { icon: Megaphone, text: "Reusable campaigns" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <item.icon size={16} weight="fill" className="text-[#002FA7]" />
                <span className="text-gray-600 font-medium">{item.text}</span>
              </div>
            ))}
          </motion.div>

          {/* Product mockup — overflow hidden on mobile */}
          <motion.div {...fade} transition={{ duration: 0.6, delay: 0.25 }} className="mt-12 overflow-x-auto">
            <div className="min-w-[700px]">
              <ProductMockup />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 bg-gray-900 text-white" data-testid="problem">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fade}>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Manual local prospecting is a<br />
              <span className="text-[#6B8AFF]">brutal use of time.</span>
            </h2>
          </motion.div>
          <div className="mt-14 grid md:grid-cols-2 gap-12">
            <motion.div {...fade} transition={{ delay: 0.1 }}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500 mb-6">The old way</p>
              <div className="space-y-4">
                {["Google businesses in an area one by one","Click through each website to check if they're a fit","Try to find contact info","Assess their site for problems you can mention","Write a personalized email from scratch","Repeat 50-100 times per city"].map((s, i) => (
                  <div key={i} className="flex items-start gap-3 text-gray-400"><X size={16} weight="bold" className="text-red-400 mt-1 flex-shrink-0" /><span>{s}</span></div>
                ))}
                <p className="text-sm text-gray-500 mt-4 pt-4 border-t border-gray-800">That's 3-4 hours per city. Most people give up after 10 prospects.</p>
              </div>
            </motion.div>
            <motion.div {...fade} transition={{ delay: 0.2 }}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B8AFF] mb-6">With Pluckm</p>
              <div className="space-y-4">
                {["Pick a niche and city. Hit discover.","Get a filtered list of real businesses with websites","One-click audit scores every prospect","AI generates emails using real findings + your pitch","Export or send. Move on to the next city."].map((s, i) => (
                  <div key={i} className="flex items-start gap-3 text-white"><Check size={16} weight="bold" className="text-emerald-400 mt-1 flex-shrink-0" /><span>{s}</span></div>
                ))}
                <p className="text-sm text-[#6B8AFF] mt-4 pt-4 border-t border-gray-800 font-medium">Same city. 4 minutes instead of 4 hours.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── WORKFLOW WALKTHROUGH (trust section) ── */}
      <section className="py-14 px-4 sm:px-6 bg-white border-b border-gray-100" data-testid="workflow">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fade} className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">One prospect, start to finish</p>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Choose campaign. Find prospects. Audit sites. Generate email.
            </h3>
          </motion.div>
          <motion.div {...fade} transition={{ delay: 0.1 }}>
            <WorkflowWalkthrough />
          </motion.div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" className="scroll-mt-20 py-20 md:py-28 px-4 sm:px-6" data-testid="how-it-works">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fade} className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">How it works</p>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Five steps. Real outreach.
            </h2>
          </motion.div>
          <div className="space-y-0">
            {[
              { num: "01", title: "Create your campaign", desc: "Define your offer once. Product name, pitch angle, CTA, sender info. Save it. Reuse it across every city you prospect.", icon: Megaphone },
              { num: "02", title: "Find businesses", desc: "Choose a niche — dentist, plumber, chiropractor, anything. Enter a city. Pluckm searches the web and returns real businesses, filtered to exclude directories and junk.", icon: Crosshair },
              { num: "03", title: "Audit their website", desc: "One click runs 10 real checks: SSL, load time, mobile-friendliness, meta tags, structured data, and more. Each prospect gets a score. You instantly see who has problems.", icon: Lightning },
              { num: "04", title: "Generate personalized emails", desc: "AI writes a unique email for each prospect using your campaign context and their specific audit findings. Real numbers. Real issues. Not Dear Business Owner.", icon: Envelope },
              { num: "05", title: "Export or send", desc: "Copy individual emails. Export everything as CSV for your CRM. Or send directly via SendGrid. Your outreach, your workflow.", icon: DownloadSimple },
            ].map((step, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.06 }} className="flex items-start gap-6 md:gap-10 py-8 border-b border-gray-100">
                <span className="text-5xl md:text-7xl font-black text-gray-100 leading-none select-none" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{step.num}</span>
                <div className="flex-1 pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <step.icon size={20} weight="duotone" className="text-[#002FA7]" />
                    <h3 className="text-lg md:text-xl font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{step.title}</h3>
                  </div>
                  <p className="text-gray-500 leading-relaxed max-w-xl">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY EMAILS WORK ── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 bg-[#F5F6F8]" data-testid="email-comparison">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fade} className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Why it works</p>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Outreach grounded in real problems,<br /><span className="text-[#002FA7]">not empty fluff.</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div {...fade} transition={{ delay: 0.1 }} className="bg-white border border-gray-200 p-8">
              <div className="flex items-center gap-2 mb-4">
                <X size={16} weight="bold" className="text-red-400" />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-red-400">Generic cold email</span>
              </div>
              <div className="bg-gray-50 border border-gray-100 p-6 text-sm text-gray-400 leading-relaxed italic">
                "Hi, I noticed your business could benefit from our services. We help local businesses improve their online presence and attract more customers. Would you like to schedule a call to discuss how we can help you grow?"
              </div>
              <p className="mt-4 text-xs text-gray-400">Vague. Forgettable. No reason to reply.</p>
            </motion.div>
            <motion.div {...fade} transition={{ delay: 0.2 }} className="bg-white border-2 border-[#002FA7] p-8 relative">
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#002FA7]" />
              <div className="flex items-center gap-2 mb-4">
                <Check size={16} weight="bold" className="text-emerald-500" />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#002FA7]">Pluckm email</span>
              </div>
              <div className="bg-blue-50/50 border border-blue-100 p-6 text-sm text-gray-700 leading-relaxed">
                "Hi — I took a look at <strong>midtownsmilesnashville.com</strong> and noticed a few things: your page loads in <strong>3.8 seconds on mobile</strong> (patients leave after 3), you're missing a <strong>meta description</strong> (Google auto-generates one, usually poorly), and there's no <strong>structured data</strong> — so your hours and address may not appear in local search results. Your overall score is <strong>58/100</strong>. Happy to send the full breakdown."
              </div>
              <p className="mt-4 text-xs text-[#002FA7] font-medium">Specific. Credible. Built from real audit data.</p>
            </motion.div>
          </div>
          <motion.p {...fade} className="text-center text-gray-400 text-sm mt-8">Every email references actual findings from that prospect's website. That's what gets replies.</motion.p>
        </div>
      </section>

      {/* ── CAMPAIGNS ── */}
      <section className="py-20 md:py-28 px-4 sm:px-6" data-testid="campaigns">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fade} className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Campaigns</p>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>One engine. Multiple offers.</h2>
            <p className="mt-4 text-gray-500 max-w-xl mx-auto">Same discovery. Same audits. Completely different outreach depending on what you're selling.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Website Redesign", type: "Web design agency", pitch: "Your site was built in 2018 and it shows. I found 4 issues that are turning away customers.", color: "#002FA7" },
              { name: "Reputation Builder", type: "Review management", pitch: "You have 12 Google reviews. Your competitor down the street has 87. We close that gap.", color: "#059669" },
              { name: "Your next offer", type: "Whatever you sell", pitch: "Define your product, pitch, and CTA. The engine adapts. Same prospects, different angle.", color: "#7C3AED" },
            ].map((c, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.1 }} className="bg-white border border-gray-200 p-6 hover:border-gray-300 transition-colors">
                <div className="w-8 h-8 mb-4 flex items-center justify-center" style={{ background: c.color + "12" }}>
                  <Megaphone size={16} style={{ color: c.color }} weight="duotone" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{c.name}</h3>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">{c.type}</p>
                <p className="text-sm text-gray-500 italic">"{c.pitch}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" className="scroll-mt-20 py-20 md:py-28 px-4 sm:px-6 bg-gray-900 text-white" data-testid="features">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fade} className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B8AFF] mb-3">Features</p>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Prospecting, auditing, and outreach<br />in one workflow.
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Crosshair, title: "Multi-niche discovery", desc: "Find chiropractors, dentists, plumbers, lawyers — or any custom business type — with a single search." },
              { icon: Buildings, title: "City-based search", desc: "Enter a city name. No ZIP codes required. Get a filtered list of real businesses with websites." },
              { icon: ShieldCheck, title: "Real website audits", desc: "10 checks against live websites: SSL, load time, mobile, meta tags, structured data. Numbers you can reference in outreach." },
              { icon: Megaphone, title: "Campaign system", desc: "Define your offer once — product, pitch, CTA, sender. Switch campaigns and every generated email adapts automatically." },
              { icon: Cpu, title: "AI email generation", desc: "Each email is written from your campaign context and that prospect's specific audit data. No two emails are the same." },
              { icon: Lightning, title: "Batch processing", desc: "Discover, audit all, generate all emails. Three clicks to prospect an entire city." },
              { icon: Funnel, title: "Smart filtering", desc: "100+ directory domains blocked. Flag irrelevant results and they never appear again. The tool learns with use." },
              { icon: DownloadSimple, title: "CSV export", desc: "Download prospects, scores, and full email drafts in one file. Ready for your CRM or email tool." },
              { icon: Envelope, title: "Email sending", desc: "Connect SendGrid to send directly from the dashboard. Or copy and paste into your existing workflow." },
            ].map((f, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.04 }} className="bg-gray-800/50 border border-gray-700/50 p-6 hover:border-gray-600 transition-colors">
                <f.icon size={24} weight="duotone" className="text-[#6B8AFF] mb-4" />
                <h3 className="font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SPEED ── */}
      <section className="py-20 md:py-28 px-4 sm:px-6" data-testid="speed">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div {...fade}>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              What used to take hours<br /><span className="text-[#002FA7]">now takes minutes.</span>
            </h2>
            <p className="mt-6 text-gray-500 max-w-xl mx-auto text-lg">Less time hunting. More time selling. More prospects processed. Less mental fatigue from repetitive research.</p>
          </motion.div>
          <motion.div {...fade} transition={{ delay: 0.2 }} className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { num: "4 min", label: "per city instead of 4 hours" },
              { num: "10", label: "real checks per website audit" },
              { num: "1 click", label: "to audit every prospect" },
              { num: "100+", label: "junk domains auto-blocked" },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-gray-200 p-6">
                <p className="text-3xl md:text-4xl font-black text-[#002FA7]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.num}</p>
                <p className="text-xs text-gray-400 mt-2 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="scroll-mt-20 py-20 md:py-28 px-4 sm:px-6 bg-[#F5F6F8]" data-testid="pricing">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fade} className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-3">Early access pricing</p>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Plans for solo operators, pros, and agencies.
            </h2>
            <p className="mt-3 text-sm text-gray-400">Pricing is subject to change during beta. Early access members lock in their rate.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { name: "Starter", price: "$0", period: "during beta", features: ["1 discovery per day", "5 email generations", "1 campaign", "CSV export"], cta: "Join the Waitlist", highlight: false },
              { name: "Pro", price: "$49", period: "/month", features: ["Unlimited discovery", "Unlimited email generation", "Unlimited campaigns", "Batch processing", "CSV export", "Priority support"], cta: "Get Early Access", highlight: true },
              { name: "Agency", price: "$149", period: "/month", features: ["Everything in Pro", "Email sending via SendGrid", "Team access (coming soon)", "Dedicated support"], cta: "Get Early Access", highlight: false },
            ].map((plan, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.1 }}
                className={`p-8 ${plan.highlight ? "bg-[#002FA7] text-white border-2 border-[#002FA7] relative" : "bg-white border border-gray-200"}`}>
                {plan.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-400 text-gray-900 text-[10px] font-bold uppercase tracking-wider px-3 py-1">Most popular</div>}
                <p className="text-sm font-medium mb-1">{plan.name}</p>
                <p className="text-4xl font-black mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{plan.price}<span className="text-lg font-normal opacity-60 ml-1">{plan.period}</span></p>
                <div className="mt-6 space-y-3 mb-8">
                  {plan.features.map((f, j) => (
                    <div key={j} className="flex items-center gap-2 text-sm">
                      <Check size={14} weight="bold" className={plan.highlight ? "text-emerald-300" : "text-emerald-500"} />
                      <span className={plan.highlight ? "text-blue-100" : "text-gray-600"}>{f}</span>
                    </div>
                  ))}
                </div>
                <button className={`w-full py-3 text-sm font-semibold transition-all ${plan.highlight ? "bg-white text-[#002FA7] hover:bg-gray-100" : "bg-[#002FA7] text-white hover:bg-[#002482]"}`} data-testid={`pricing-cta-${plan.name.toLowerCase()}`}>
                  {plan.cta}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 md:py-28 px-4 sm:px-6" data-testid="faq">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fade} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Frequently asked questions</h2>
          </motion.div>
          <div className="space-y-0">
            {[
              { q: "Who is Pluckm for?", a: "Anyone who sells to local businesses — SEO consultants, marketing agencies, SaaS founders, freelancers, sales teams. If you spend time finding local leads and writing outreach, this is built for you." },
              { q: "Does it work for any niche?", a: "Yes. Chiropractors, dentists, plumbers, electricians, lawyers, vets, accountants — or type any custom business type. The discovery and audit engine is niche-agnostic." },
              { q: "Are the audits real?", a: "Yes. We make actual HTTP requests to each website and check SSL, load time, mobile viewport, meta tags, H1 structure, image alt tags, page size, HTTPS redirect, and structured data. Real checks, real numbers." },
              { q: "Are the emails generic?", a: "No. Each email is generated individually using your campaign context and that specific prospect's audit findings. The AI references actual numbers and issues unique to their website." },
              { q: "Can I use multiple campaigns?", a: "Yes. Create as many as you want. Selling SEO in the morning and appointment scheduling in the afternoon? Switch campaigns and every email adapts." },
              { q: "Can I export prospects and emails?", a: "Yes. One-click CSV export includes all prospect data, audit scores, and generated email content. Ready for your CRM or spreadsheet." },
              { q: "How is this different from Apollo or ZoomInfo?", a: "Those are broad B2B prospecting tools built for enterprise sales. Pluckm is purpose-built for local business discovery — it finds the dentist in Nashville, not the VP of Marketing at a tech company." },
              { q: "Is this in beta?", a: "Yes. The core product works — discovery, auditing, email generation, campaigns, export. We're refining the experience and onboarding early users at a locked-in rate." },
            ].map((faq, i) => (
              <motion.div key={i} {...fade} transition={{ delay: i * 0.03 }} className="border-b border-gray-100">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between py-5 text-left" data-testid={`faq-${i}`}>
                  <span className="text-sm font-semibold text-gray-900 pr-4">{faq.q}</span>
                  <CaretDown size={16} className={`text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="pb-5">
                    <p className="text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 md:py-28 px-4 sm:px-6 bg-gray-900 text-white" data-testid="final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div {...fade}>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Stop doing local prospecting<br /><span className="text-[#6B8AFF]">the hard way.</span>
            </h2>
            <p className="mt-6 text-gray-400 text-lg max-w-xl mx-auto">Find businesses. Identify real issues. Send outreach with an actual reason behind it.</p>
            <div className="mt-10">
              <a href="#pricing" className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 text-sm font-bold hover:bg-gray-100 transition-all" data-testid="final-cta-btn">
                Get Early Access <ArrowRight size={16} weight="bold" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-10 px-4 sm:px-6 border-t border-gray-100 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-sm font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            <span className="text-[#002FA7]">Pluck</span><span className="text-gray-900">m</span>
          </span>
          <p className="text-xs text-gray-400">Find the right businesses, uncover real opportunities, and generate custom outreach that gets replies.</p>
        </div>
      </footer>
    </div>
  );
}
